import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { ESTATUS_HEX, EstatusBadge } from "@/components/ContratosDetalleModal";
import { MultiSelect } from "@/components/MultiSelect";
import {
  type ContratoGanttRow,
  getContratosCatalogos,
  getContratosGantt,
} from "@/lib/api";
import { formatDate } from "@/lib/utils";

const PAGE_SIZE = 50;
type AgruparPor = "emisor" | "adquiriente";

function todayISO(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function ContratosGanttPage() {
  const [emisores, setEmisores] = useState<string[]>([]);
  const [adquirientes, setAdquirientes] = useState<string[]>([]);
  // Default: activos (no terminados). Lo seteamos cuando el catálogo carga.
  const [estatus, setEstatus] = useState<string[]>(["ACE", "CSA"]);
  const [soloVigentes, setSoloVigentes] = useState(true);
  const [agruparPor, setAgruparPor] = useState<AgruparPor>("emisor");
  const [page, setPage] = useState(0);

  // Cuando los filtros emisor y adquiriente son el MISMO conjunto, el backend
  // los trata como OR (involucra como cualquier parte). En ese caso agrupar
  // por uno u otro no es informativo — los contratos pueden tener al filtro
  // como emisor O como adquiriente, mezcladamente.
  const agruparDisabled =
    emisores.length > 0 &&
    adquirientes.length > 0 &&
    emisores.length === adquirientes.length &&
    new Set(emisores).size === new Set([...emisores, ...adquirientes]).size;
  const agruparEfectivo: AgruparPor = agruparDisabled ? "emisor" : agruparPor;

  const catalogos = useQuery({
    queryKey: ["contratos-catalogos"],
    queryFn: getContratosCatalogos,
  });

  const gantt = useQuery({
    queryKey: [
      "contratos-gantt",
      emisores,
      adquirientes,
      estatus,
      soloVigentes,
      agruparEfectivo,
      page,
    ],
    queryFn: () =>
      getContratosGantt({
        emisores: emisores.length ? emisores : undefined,
        adquirientes: adquirientes.length ? adquirientes : undefined,
        estatus: estatus.length ? estatus : undefined,
        // Si "solo vigentes", traemos contratos cuyo periodo cruza con hoy.
        desde: soloVigentes ? todayISO() : undefined,
        hasta: soloVigentes ? todayISO() : undefined,
        agrupar_por: agruparEfectivo,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  });

  const items = gantt.data?.items ?? [];
  const total = gantt.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const dominio = useMemo(() => computeDominio(items), [items]);

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <MultiSelect
          label="Emisor"
          options={catalogos.data?.emisores ?? []}
          selected={emisores}
          onChange={(s) => {
            setEmisores(s);
            setPage(0);
          }}
        />
        <MultiSelect
          label="Adquiriente"
          options={catalogos.data?.adquirientes ?? []}
          selected={adquirientes}
          onChange={(s) => {
            setAdquirientes(s);
            setPage(0);
          }}
        />
        <MultiSelect
          label="Estatus"
          options={catalogos.data?.estatus ?? []}
          selected={estatus}
          onChange={(s) => {
            setEstatus(s);
            setPage(0);
          }}
        />
        <div className="flex flex-col">
          <span className="text-xs font-medium text-slate-600 mb-1">Agrupar por</span>
          <div
            className={`inline-flex border rounded overflow-hidden ${
              agruparDisabled ? "opacity-50" : ""
            }`}
            title={
              agruparDisabled
                ? "Emisor y adquiriente son el mismo — el agrupamiento no aplica"
                : undefined
            }
          >
            {(["emisor", "adquiriente"] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                disabled={agruparDisabled}
                onClick={() => {
                  setAgruparPor(opt);
                  setPage(0);
                }}
                className={`flex-1 px-3 py-1.5 text-sm capitalize ${
                  agruparPor === opt && !agruparDisabled
                    ? "bg-blue-600 text-white"
                    : "bg-white hover:bg-slate-50 disabled:hover:bg-white disabled:cursor-not-allowed"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-medium text-slate-600 mb-1">Vigencia</span>
          <label className="inline-flex items-center gap-2 text-sm border rounded px-2 py-1.5">
            <input
              type="checkbox"
              checked={soloVigentes}
              onChange={(e) => {
                setSoloVigentes(e.target.checked);
                setPage(0);
              }}
            />
            <span>Solo vigentes hoy</span>
          </label>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 text-sm">
          <h3 className="font-medium">Gantt de contratos</h3>
          <div className="flex items-center gap-4">
            <Leyenda />
            <span className="text-slate-500">
              {total.toLocaleString("es-MX")} contratos
            </span>
          </div>
        </div>

        {gantt.isLoading && (
          <div className="py-16 text-center text-slate-500">Cargando…</div>
        )}
        {gantt.isError && (
          <div className="py-16 text-center text-red-600">
            Error: {(gantt.error as Error).message}
          </div>
        )}
        {gantt.data && items.length === 0 && (
          <div className="py-16 text-center text-slate-500">
            Sin contratos para los filtros aplicados.
          </div>
        )}
        {items.length > 0 && dominio && (
          <Gantt items={items} dominio={dominio} agruparPor={agruparEfectivo} />
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm border-t mt-3 pt-3">
            <div className="text-slate-500">
              Página {page + 1} de {totalPages} · {PAGE_SIZE} por página
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="border rounded px-3 py-1 disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                ← Anterior
              </button>
              <button
                type="button"
                className="border rounded px-3 py-1 disabled:opacity-50"
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPages}
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Leyenda() {
  return (
    <div className="flex items-center gap-3 text-xs">
      {Object.entries(ESTATUS_HEX).map(([k, color]) => (
        <span key={k} className="inline-flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded"
            style={{ backgroundColor: color }}
          />
          {k}
        </span>
      ))}
    </div>
  );
}

type Dominio = { min: Date; max: Date; spanMs: number };

function computeDominio(items: ContratoGanttRow[]): Dominio | null {
  if (items.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const it of items) {
    if (it.fecha_inicio) {
      const t = parseLocalDate(it.fecha_inicio).getTime();
      if (t < min) min = t;
    }
    if (it.fecha_fin) {
      const t = parseLocalDate(it.fecha_fin).getTime();
      if (t > max) max = t;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min: new Date(min), max: new Date(max), spanMs: max - min };
}

function parseLocalDate(iso: string): Date {
  // YYYY-MM-DD interpretado como local (no UTC), para evitar shift de zona.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  return new Date(iso);
}

const ROW_HEIGHT = 22;
const GROUP_HEADER_HEIGHT = 22;
const LABEL_WIDTH = 280;
const RIGHT_PADDING = 16;
const HEADER_AXIS_HEIGHT = 30;
const MIN_CHART_WIDTH = 400; // si la ventana es más chica, hace scroll horizontal

type Grupo = {
  key: string;
  yStart: number; // Y donde inicia el header del grupo
  yEnd: number; // Y donde termina la última fila del grupo
  rows: { item: ContratoGanttRow; y: number }[];
};

function Gantt({
  items,
  dominio,
  agruparPor,
}: {
  items: ContratoGanttRow[];
  dominio: Dominio;
  agruparPor: AgruparPor;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(900);
  const [hover, setHover] = useState<{
    item: ContratoGanttRow;
    x: number; // posición en viewport (clientX/Y) — el tooltip va fixed
    y: number;
  } | null>(null);

  // Mide el ancho disponible y se reajusta cuando cambia el tamaño de la ventana
  // o aparecen scrollbars. ResizeObserver dispara incluso si el body cambia.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setContainerW(el.clientWidth || 900);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Agrupamos los items consecutivos por la columna seleccionada.
  const grupos = useMemo<Grupo[]>(() => {
    const out: Grupo[] = [];
    let cursorY = HEADER_AXIS_HEIGHT;
    let current: Grupo | null = null;
    for (const it of items) {
      const key =
        (agruparPor === "emisor" ? it.emisor : it.adquiriente) ??
        `(sin ${agruparPor})`;
      if (!current || current.key !== key) {
        if (current) current.yEnd = cursorY;
        current = { key, yStart: cursorY, yEnd: cursorY, rows: [] };
        out.push(current);
        cursorY += GROUP_HEADER_HEIGHT;
      }
      current.rows.push({ item: it, y: cursorY });
      cursorY += ROW_HEIGHT;
    }
    if (current) current.yEnd = cursorY;
    return out;
  }, [items, agruparPor]);

  const yearTicks = useMemo(() => {
    const ticks: { date: Date; label: string }[] = [];
    const startY = dominio.min.getFullYear();
    const endY = dominio.max.getFullYear();
    for (let y = startY; y <= endY + 1; y++) {
      ticks.push({ date: new Date(y, 0, 1), label: String(y) });
    }
    return ticks;
  }, [dominio]);

  // chartWidth se ajusta al espacio disponible; si es muy chico, scrolleamos.
  const chartWidth = Math.max(MIN_CHART_WIDTH, containerW - LABEL_WIDTH - RIGHT_PADDING);
  const totalWidth = LABEL_WIDTH + chartWidth + RIGHT_PADDING;
  const totalHeight =
    HEADER_AXIS_HEIGHT +
    grupos.length * GROUP_HEADER_HEIGHT +
    items.length * ROW_HEIGHT;

  const xFor = (d: Date) =>
    LABEL_WIDTH + ((d.getTime() - dominio.min.getTime()) / dominio.spanMs) * chartWidth;

  const today = new Date();
  const todayX =
    today.getTime() >= dominio.min.getTime() && today.getTime() <= dominio.max.getTime()
      ? xFor(today)
      : null;

  const labelKey: keyof ContratoGanttRow =
    agruparPor === "emisor" ? "adquiriente" : "emisor";

  return (
    <div ref={containerRef} className="overflow-x-auto relative">
      <svg
        width={totalWidth}
        height={totalHeight}
        className="select-none"
        onMouseLeave={() => setHover(null)}
      >
        {/* Bandas de fondo por grupo (alternadas) */}
        {grupos.map((g, i) => (
          <rect
            key={`band-${i}`}
            x={0}
            y={g.yStart}
            width={totalWidth}
            height={g.yEnd - g.yStart}
            fill={i % 2 === 0 ? "#f8fafc" : "#ffffff"}
          />
        ))}

        {/* Eje X con años */}
        <g>
          {yearTicks.map((t) => {
            const x = xFor(t.date);
            return (
              <g key={t.label}>
                <line
                  x1={x}
                  y1={20}
                  x2={x}
                  y2={totalHeight}
                  stroke="#e2e8f0"
                  strokeWidth={1}
                />
                <text
                  x={x}
                  y={14}
                  fontSize={11}
                  textAnchor="middle"
                  fill="#64748b"
                >
                  {t.label}
                </text>
              </g>
            );
          })}
        </g>

        {/* Línea de hoy */}
        {todayX != null && (
          <g>
            <line
              x1={todayX}
              y1={20}
              x2={todayX}
              y2={totalHeight}
              stroke="#ef4444"
              strokeWidth={1}
              strokeDasharray="3 2"
            />
            <text x={todayX + 4} y={28} fontSize={10} fill="#ef4444" fontWeight="500">
              hoy
            </text>
          </g>
        )}

        {/* Headers de grupo + filas */}
        {grupos.map((g, gi) => (
          <g key={`g-${gi}`}>
            <rect
              x={0}
              y={g.yStart}
              width={LABEL_WIDTH}
              height={GROUP_HEADER_HEIGHT}
              fill="#e2e8f0"
            />
            <text
              x={8}
              y={g.yStart + GROUP_HEADER_HEIGHT / 2 + 4}
              fontSize={11}
              fontWeight={600}
              fill="#1e293b"
            >
              {truncate(g.key, 38)}
            </text>
            <text
              x={LABEL_WIDTH - 8}
              y={g.yStart + GROUP_HEADER_HEIGHT / 2 + 4}
              fontSize={10}
              textAnchor="end"
              fill="#64748b"
            >
              {g.rows.length} contrato{g.rows.length === 1 ? "" : "s"}
            </text>
            <line
              x1={0}
              y1={g.yStart + GROUP_HEADER_HEIGHT}
              x2={totalWidth}
              y2={g.yStart + GROUP_HEADER_HEIGHT}
              stroke="#cbd5e1"
              strokeWidth={1}
            />

            {/* Filas del grupo */}
            {g.rows.map(({ item, y }) => {
              const startD = item.fecha_inicio ? parseLocalDate(item.fecha_inicio) : null;
              const endD = item.fecha_fin ? parseLocalDate(item.fecha_fin) : null;
              if (!startD || !endD) return null;
              const x1 = xFor(startD);
              const x2 = xFor(endD);
              const w = Math.max(2, x2 - x1);
              const color = ESTATUS_HEX[item.estatus ?? ""] ?? "#94a3b8";
              const otherParty = item[labelKey] as string | null;
              const label =
                (item.folio ?? `#${item.id_contrato}`) +
                (otherParty ? ` · ${truncate(otherParty, 22)}` : "");

              return (
                <g
                  key={item.id_contrato}
                  onMouseMove={(e) =>
                    setHover({
                      item,
                      // clientX/Y son coords de viewport — usamos `position: fixed`
                      // para que el tooltip no se recorte por overflow del scroll.
                      x: e.clientX,
                      y: e.clientY,
                    })
                  }
                >
                  <text
                    x={LABEL_WIDTH - 8}
                    y={y + ROW_HEIGHT / 2 + 4}
                    fontSize={11}
                    textAnchor="end"
                    fill="#475569"
                  >
                    {label}
                  </text>
                  <rect
                    x={x1}
                    y={y + 3}
                    width={w}
                    height={ROW_HEIGHT - 6}
                    rx={3}
                    fill={color}
                    opacity={0.9}
                  />
                </g>
              );
            })}
          </g>
        ))}
      </svg>

      {hover && (
        <div
          className="fixed z-50 bg-white border shadow-lg rounded p-2 text-xs pointer-events-none"
          style={{
            // viewport coords; ajustamos si el tooltip se saldría por la derecha o el fondo.
            left: Math.min(hover.x + 12, window.innerWidth - 296),
            top: Math.min(hover.y + 12, window.innerHeight - 140),
            maxWidth: 280,
          }}
        >
          <div className="font-mono text-xs font-medium mb-1">
            {hover.item.folio ?? `#${hover.item.id_contrato}`}
          </div>
          <div className="mb-1">
            <EstatusBadge estatus={hover.item.estatus} />
            {hover.item.tipo_contrato && (
              <span className="ml-2 text-slate-500">{hover.item.tipo_contrato}</span>
            )}
          </div>
          <div className="space-y-0.5 text-slate-700">
            <div>
              <strong>Folio real:</strong>{" "}
              <span className="font-mono">{hover.item.folio_real ?? "—"}</span>
            </div>
            <div>
              <strong>Emisor:</strong> {hover.item.emisor ?? "—"}
            </div>
            <div>
              <strong>Adq.:</strong> {hover.item.adquiriente ?? "—"}
            </div>
            <div>
              <strong>Vigencia:</strong> {formatDate(hover.item.fecha_inicio)} →{" "}
              {formatDate(hover.item.fecha_fin)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
